<template>
  <MoleculeLabel
    :text="t('subscription.billingMode.monthlyPayment.label')"
    label-for="monthly-payment"
    class="mb-1 mt-8 font-medium"
  />
  <OrganismSegmentedControl
    v-model="modelValue"
    type="radio"
    segmented-control-name="monthly-payment"
    :controls="monthlyPaymentOptions"
    tooltip-deactivated
    data-cy="billing-mode-monthly-payment-input"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSubscriptionStore, useToastSnackbarStore } from '@/stores';

const { t } = useI18n();

const { addErrorToast } = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const { currentSubscription, getCurrentDirectDebit } = storeToRefs(subscriptionStore);

const DEFAULT_MONTHLY_PAYMENT_INDEX = 2;

const allowedMonthlyPayment: Ref<{ min: number; max: number } | undefined> = ref();

const monthlyPaymentValues = computed(() => {
  if (!allowedMonthlyPayment.value) {
    return [];
  }

  const { min, max } = allowedMonthlyPayment.value;
  let lastValue = min;
  const options = [min];

  while (lastValue < max) {
    const step = (max - min) * 0.25;
    const value = Math.floor(min + step * options.length);
    options.push(value);
    lastValue = value;
  }

  return options;
});

const monthlyPaymentOptions = computed(() =>
  monthlyPaymentValues.value.map((value, index) => ({
    id: `payment-option-${index}`,
    label: `${value}€`,
    value,
  }))
);

const modelValue = defineModel<number | undefined>();

onMounted(() => {
  allowedMonthlyPayment.value =
    currentSubscription.value.estimatedAnnualBudgetBreakdown?.allowedMonthlyPayment;

  if (!allowedMonthlyPayment.value) {
    addErrorToast({
      id: 'no-allowed-monthly-payment-found',
      title: t('subscription.billingMode.monthlyPayment.noMonthlyPaymentFound.title'),
      content: t('subscription.billingMode.monthlyPayment.noMonthlyPaymentFound.content'),
    });
    return;
  }

  // Emit the initial displayed value to the parent so it is available on submit
  // even if the user does not interact with the selector.
  modelValue.value =
    getCurrentDirectDebit.value?.selectedLinearMonthlyPayment ??
    monthlyPaymentOptions.value[DEFAULT_MONTHLY_PAYMENT_INDEX]?.value;
});
</script>

<style>
/* TODO : remove once DS fixed paddings */
[data-testid='rgv-segmented-control-item'] label {
  padding-left: 0;
  padding-right: 0;
}
</style>
